<template>
  <div>
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color:#fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color:#fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <div class="full-screen-map-wrapper">
      <!-- ── Leaflet Map ─────────────────────────────────────────────── -->
      <BaseLeafletMap
        title="Users Map"
        :layers="mappedLayers"
        :statistics="mapStats"
        :loading="appLoading"
        :toolbar="{ search: true, fullscreen: true, fitBounds: true, satellite: true }"
        height="100vh"
        @marker-click="onMarkerClick"
        @layer-toggle="onLayerToggle"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import BaseLeafletMap from '@/components/maps/BaseLeafletMap.vue';
import UserPopup      from '@/components/maps/popups/UserPopup.vue';
import { layerFactory } from '@/components/maps/utils/layerFactory';
import { LAYER_TYPES }  from '@/components/maps/constants/layerTypes';
import { STATUS_COLORS } from '@/components/maps/constants/statusColors';

export default {
  components: { BaseLeafletMap },

  data() {
    return {
      appLoading:   false,
      ServerError:  false,
      showSnackBar: false,
      timeout:      5000,
      msg:          null,
      data:         [],
      totalcount:   0,
    };
  },

  computed: {
    usersWithoutLocation() {
      if (!this.data || !this.data.length) return 0;
      return this.data.filter(u => !u.location || u.location.length !== 2).length;
    },

    usersWithLocation() {
      return this.data.filter(u => u.location && u.location.length === 2).length;
    },

    activeUsers() {
      return this.data.filter(u => u.isActive || u.active).length;
    },

    mappedLayers() {
      return [
        layerFactory({
          id:         LAYER_TYPES.USERS,
          title:      'Users',
          color:      STATUS_COLORS.USER,
          clustered:  true,
          searchable: true,
          legend: [{ color: STATUS_COLORS.USER, label: 'App User' }],
          markers: (() => {
            const rawMarkers = this.data
              .filter(u => u.location && u.location.length === 2)
              .map(u => {
                return {
                  id:           u._id,
                  lat:          u.location[1],
                  lng:          u.location[0],
                  layer:        LAYER_TYPES.USERS,
                  popup:        UserPopup,
                  searchFields: [u.name, u.phone, u.district, u.email].filter(Boolean),
                  data:         u,
                };
              });

            const locGroups = {};
            rawMarkers.forEach(m => {
              const key = `${m.lat},${m.lng}`;
              if (!locGroups[key]) locGroups[key] = [];
              locGroups[key].push(m);
            });
            
            const finalMarkers = [];
            Object.values(locGroups).forEach(group => {
              if (group.length === 1) {
                finalMarkers.push(group[0]);
              } else {
                const radius = 0.00015 + (group.length * 0.000005);
                group.forEach((m, i) => {
                  const angle = (i / group.length) * 2 * Math.PI;
                  m.lat += Math.cos(angle) * radius;
                  m.lng += Math.sin(angle) * radius;
                  finalMarkers.push(m);
                });
              }
            });
            return finalMarkers;
          })(),
        }),
      ];
    },

    mapStats() {
      return [
        { title: 'Total Users',  value: this.totalcount,          icon: 'mdi-account-group',   color: STATUS_COLORS.USER      },
        { title: 'With GPS',     value: this.usersWithLocation,    icon: 'mdi-map-marker-check', color: STATUS_COLORS.AVAILABLE },
        { title: 'Without GPS',  value: this.usersWithoutLocation, icon: 'mdi-map-marker-off',   color: STATUS_COLORS.OFFLINE   },
      ];
    },
  },

  beforeMount() {
    this.getData();
  },

  methods: {
    getData() {
      this.appLoading = true;
      axios({
        method:  'GET',
        url:     '/user/allusers/v1',
        headers: { 'x-auth-token': localStorage.getItem('token') },
        params:  { type: 'map' },
      })
        .then(response => {
          if (response.data.status === true) {
            this.data       = response.data.data;
            this.totalcount = response.data.count;
          } else {
            this.msg          = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch(err => {
          this.ServerError = true;
          console.log(err);
        })
        .finally(() => { this.appLoading = false; });
    },

    onMarkerClick({ data }) {
      // Optional: analytics or side-panel update
      console.log('User marker clicked:', data.name);
    },

    onLayerToggle({ layerId, visible }) {
      console.log('Layer toggle:', layerId, visible);
    },

    formatDate(date) {
      if (!date) return '-';
      const [year, month, day] = date.slice(0, 10).split('-');
      return `${day}-${month}-${year}`;
    },
  },
};
</script>
<style scoped>
.full-screen-map-wrapper {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

.floating-warning {
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 1200;
  max-width: 320px;
  pointer-events: auto;
}

.warning-content {
  display: flex;
  align-items: flex-start;
  background: #1A1A1A;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #333333;
  border-left: 4px solid #F59E0B;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.warning-content:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.25);
}

.warning-text {
  font-family: 'Poppins', poppinsregular, sans-serif;
  font-size: 12.5px;
  color: #A1A1AA;
  line-height: 1.5;
}

.warning-text strong {
  color: #FFFFFF;
  font-weight: 700;
}
</style>

<style scoped>
.heading {
  font-family: poppinsregular;
  font-size: 20px;
  font-weight: 900;
}
.subheading {
  font-family: poppinsregular;
  font-size: 15px;
  font-weight: 500;
}
</style>